import React, { useEffect, useMemo, useRef, useState } from "react";
/* ============================ CONFIG ============================ */
const DATA_SOURCES = {
  QB: "/data/nfl/showdown/latest/qb_data.json",
  RB: "/data/nfl/showdown/latest/rb_data.json",
  WR: "/data/nfl/showdown/latest/wr_data.json",
  TE: "/data/nfl/showdown/latest/te_data.json",
  K: "/data/nfl/showdown/latest/k_data.json",
  DST: "/data/nfl/showdown/latest/dst_data.json",
};
const META_URL = "/data/nfl/showdown/latest/meta.json";
// Prepared game-log files are generated by the NFL stat puller.
// Add a season here when that season folder is available under /public/data/nfl/std/.
const GAME_LOG_YEARS = [2026, 2025];
const DEFAULT_GAME_LOG_YEAR = 2026;

const opportunityUrl = (year) => `/data/nfl/std/${year}/ff_opportunity.json`;

function gameLogUrl(year, position) {
  return `/data/nfl/std/${year}/game_logs_${String(position || "QB").toLowerCase()}.json`;
}

// Cache by season + position so changing positions or years does not redownload files.
const GAME_LOG_PREPARED_CACHE = new Map();
const GAME_LOG_REQUEST_CACHE = new Map();


const TITLES = {
  QB: "NFL — QB Data (Showdown)",
  RB: "NFL — RB Data (Showdown)",
  WR: "NFL — WR Data (Showdown)",
  TE: "NFL — TE Data (Showdown)",
  K: "NFL — K Data (Showdown)",
  DST: "NFL — DST Data (Showdown)",
};
const SHOW_TEAM_LOGOS = true;
const HIDE_PLAYER_INFO_LABEL = true;
// logos are served from /public/logos/nfl/XXX.png -> /logos/nfl/XXX.png
const LOGO_BASE = "/logos/nfl";
const LOGO_EXT = "png";
const TEAM_ABBR_MAP = { WSH: "WAS", JAC: "JAX", OAK: "LV", SD: "LAC", STL: "LAR" };
/* ============================ UI THEME ============================ */
const LIGHT_THEME = {
  radius: "rounded-2xl",
  headerBg: "bg-slate-50",
  bandBg: "bg-sky-500",
  zebraOdd: "bg-white",
  zebraEven: "bg-slate-50",
  border: "border-slate-200",
  bandBorder: "border-slate-300",
  text: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-white shadow-sm",
  page: "min-h-screen bg-slate-100 text-slate-900",
};
const DARK_THEME = {
  radius: "rounded-2xl",
  headerBg: "bg-slate-900",
  bandBg: "bg-sky-600",
  zebraOdd: "bg-slate-950",
  zebraEven: "bg-slate-900",
  border: "border-slate-800/80",
  bandBorder: "border-slate-700/80",
  text: "text-slate-100",
  subtext: "text-slate-400",
  card:
    "border border-slate-800 bg-slate-900/95 shadow-sm",
  page: "min-h-screen bg-slate-950 text-slate-100",
};
const CELL_PAD = "px-0.5 py-0.5";
const HEADER_PAD = "px-2 py-1.5";
const TEXT_SZ = "text-[11.5px]";
/* ============================ HELPERS ============================ */
const norm = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();
// normalize keys like "Comp %" vs "Comp%" vs "comp_pct"
const keynorm = (s) =>
  lower(String(s))
    .replace(/[%]/g, "pct")
    .replace(/[\s._-]/g, "")
    .replace(/[()]/g, "");
function parseNumericLike(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  if (/%$/.test(s)) {
    const n = Number(s.replace(/%/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}
function fmtTime(s) {
  const raw = norm(s);
  if (!raw) return "";
  if (/\b(am|pm)\b/i.test(raw))
    return raw
      .replace(/\s+/g, "")
      .toUpperCase()
      .replace("AM", " AM")
      .replace("PM", " PM");
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return raw;
  let hh = Number(m[1]);
  const mm = m[2];
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = ((hh + 11) % 12) + 1;
  return `${hh}:${mm} ${ampm}`;
}
function timeToMinutes(s) {
  const raw = norm(s);
  if (!raw) return null;
  const ap = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ap) {
    let h = Number(ap[1]),
      m = Number(ap[2]);
    const pm = /pm/i.test(ap[3]);
    if (h === 12) h = pm ? 12 : 0;
    else if (pm) h += 12;
    return h * 60 + m;
  }
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
function fmtCellValue(displayLabel, val) {
  const v = norm(val);
  if (!v) return "";
  // already formatted like "72%"
  if (/%$/.test(v)) return v;
  const label = String(displayLabel || "");
  // treat these as "rate" columns (stored as 0-1 in json)
  const isRateCol =
    /%/.test(label) || // Comp %, Routes%, Block%, FG%
    /^tgt\s*shr$/i.test(label) || // Tgt Shr (0-1)
    /^msair$/i.test(label); // MsAir (often 0-1)
  if (/^time$/i.test(label)) return fmtTime(v);
  // salary formatting
  if (/\b(sal|salary)\b/i.test(label)) {
    const n = parseNumericLike(v);
    return n == null ? v : Math.round(n).toLocaleString();
  }
  const n = parseNumericLike(v);
  if (n == null) return v;
  // ✅ rate -> percent display (ALWAYS one decimal now)
  if (isRateCol) {
    const pct = n <= 1.5 ? n * 100 : n; // 0.72 -> 72.0, but if already 72 keep 72.0
    return pct.toFixed(1) + "%";
  }
  // normal numeric formatting (keep your old behavior: trim .0)
  const f = n.toFixed(1);
  return f.endsWith(".0") ? f.slice(0, -2) : f;
}
function useJson(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const arr = Array.isArray(j) ? j : j?.rows || j?.data || [];
        if (alive) setData(arr);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, loading, err };
}
function useMeta(url) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();
        const j = await r.json();
        if (ok) setMeta(j);
      } catch {
        if (ok) setMeta(null);
      }
    })();
    return () => {
      ok = false;
    };
  }, [url]);
  return meta;
}
/* ============================ LOGOS ============================ */
function fixAbbr(code) {
  const c = String(code || "").toUpperCase();
  return TEAM_ABBR_MAP[c] || c;
}
function TeamWithLogo({ code }) {
  const c = fixAbbr(code);
  if (!c) return null;
  if (!SHOW_TEAM_LOGOS) return <span>{c}</span>;
  const src = `${LOGO_BASE}/${c}.${LOGO_EXT}`;
  return (
    <span className="inline-flex items-center gap-1">
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        className="h-4 w-4 shrink-0"
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
      <span>{c}</span>
    </span>
  );
}
/* ============================ YOUR EXACT COLUMN SCHEMAS ============================ */
/**
 * Each schema is: band -> [ { label, aliases: [] } ]
 * We render label always. We try to match a JSON key via aliases.
 * If not found, column still renders but values are blank.
 */
const SCHEMAS = {
  QB: {
    "Player Info": [
      { label: "POS", aliases: ["POS", "Pos", "Position"] },
      { label: "Player", aliases: ["Player", "player", "Name"] },
      { label: "DK Sal", aliases: ["DK Sal", "DK Salary", "dk_sal", "dk salary"] },
      { label: "FD Sal", aliases: ["FD Sal", "FD Salary", "fd_sal", "fd salary"] },
    ],
    "Matchup Info": [
      { label: "Team", aliases: ["Team", "team"] },
      { label: "OPP", aliases: ["OPP", "Opp", "opp", "Opponent"] },
      { label: "Home", aliases: ["Home", "home"] },
      { label: "Time", aliases: ["Time", "time", "Kickoff"] },
    ],
    Vegas: [
      { label: "O/U", aliases: ["O/U", "OU", "O-U", "Total", "Game Total"] },
      { label: "Imp Total", aliases: ["Imp Total", "Implied Total", "ImpTot", "Team Total"] },
      { label: "Spread", aliases: ["Spread", "Spr", "Line", "Spd"] },
    ],
    "Player Stats (Per Game)": [
      { label: "Pass Yds", aliases: ["Pass Yds", "PassYds", "Pass Yards", "Yards", "Pass_Yds"] },
      { label: "Pass TD", aliases: ["Pass TD", "PassTD", "Pass TDs", "TD", "Pass_TD"] },
      { label: "Int", aliases: ["Int", "INT", "Ints", "Interceptions"] },
      { label: "Pass Att", aliases: ["Pass Att", "PassAtt", "Attempts", "Att", "Pass Attempts"] },
      { label: "YPA", aliases: ["YPA", "Yds/Att", "Yards/Attempt"] },
      { label: "Comp %", aliases: ["Comp %", "Comp%", "Completion %", "Cmp%", "Comp_Pct"] },
      { label: "Rush Yds", aliases: ["Rush Yds", "RushYds", "Rsh Yds", "R. Yds", "R Yds"] },
      { label: "Rush Att", aliases: ["Rush Att", "RushAtt", "Rsh Att", "Rush Attempts"] },
      { label: "Rush TD", aliases: ["Rush TD", "RushTD", "Rsh TD", "R. TD", "R TD"] },
      { label: "YPC", aliases: ["YPC", "Yds/Carry", "Yards/Carry"] },
      { label: "QBR", aliases: ["QBR", "QB Rating", "Rating"] },
    ],
    Matchup: [
      { label: "DK Pts", aliases: ["DK Pts", "DKPts", "dk_pts", "DraftKings Pts"] },
      { label: "Rank", aliases: ["Rank", "rank"] },
      { label: "Opp Pass Yds", aliases: ["Opp Pass Yds", "OppPassYds", "Opp Pass Yards"] },
      { label: "Opp Pass TD", aliases: ["Opp Pass TD", "OppPassTD", "Opp Pass TDs"] },
      { label: "Opp INT", aliases: ["Opp INT", "OppInt", "Opp Interceptions"] },
      { label: "Opp Rush Yds", aliases: ["Opp Rush Yds", "OppRushYds", "Opp Rush Yards"] },
      { label: "Opp Rush TD", aliases: ["Opp Rush TD", "OppRushTD", "Opp Rush TDs"] },
    ],
  },
  RB: {
    "Player Info": [
      { label: "POS", aliases: ["POS", "Pos", "Position"] },
      { label: "Player", aliases: ["Player", "player", "Name"] },
      { label: "DK Sal", aliases: ["DK Sal", "DK Salary", "dk_sal"] },
      { label: "FD Sal", aliases: ["FD Sal", "FD Salary", "fd_sal"] },
    ],
    "Matchup Info": [
      { label: "Team", aliases: ["Team", "team"] },
      { label: "OPP", aliases: ["OPP", "Opp", "opp", "Opponent"] },
      { label: "Home", aliases: ["Home", "home"] },
      { label: "Time", aliases: ["Time", "time", "Kickoff"] },
    ],
    Vegas: [
      { label: "O/U", aliases: ["O/U", "OU", "Total", "Game Total"] },
      { label: "Imp Total", aliases: ["Imp Total", "Implied Total", "Team Total"] },
      { label: "Spread", aliases: ["Spread", "Line", "Spr"] },
    ],
    "Player Stats (Per Game)": [
      { label: "Rush Yds", aliases: ["Rush Yds", "RushYds", "Rushing Yds", "Rush Yards"] },
      { label: "Rush Att", aliases: ["Rush Att", "RushAtt", "Carries", "Rush Attempts"] },
      { label: "Rush TD", aliases: ["Rush TD", "RushTD", "Rushing TD", "RuTD"] },
      { label: "YPC", aliases: ["YPC", "Yds/Carry", "Yards/Carry"] },
      { label: "Rec Yds", aliases: ["Rec Yds", "RecYds", "Receiving Yds", "ReYds"] },
      { label: "Targets", aliases: ["Targets", "Tgts", "Tgt"] },
      { label: "Rec", aliases: ["Rec", "Receptions", "Catches"] },
      { label: "TD", aliases: ["TD", "Total TD", "Tds"] },
      { label: "Tgt Shr", aliases: ["Tgt Shr", "Target Share", "TgtShr"] },
      { label: "Opr", aliases: ["Opr", "OPR", "Opportunity", "OPP Rate"] },
    ],
    Matchup: [
      { label: "DK Pts", aliases: ["DK Pts", "DKPts", "dk_pts"] },
      { label: "Rank", aliases: ["Rank", "rank"] },
      { label: "Opp Rush Yards", aliases: ["Opp Rush Yards", "Opp Rush Yds", "OppRushYds"] },
      { label: "Opp Rush TD", aliases: ["Opp Rush TD", "OppRushTD"] },
      { label: "Opp Rec Yds", aliases: ["Opp Rec Yds", "OppRecYds", "Opp Receiving Yds"] },
      { label: "Opp Rec", aliases: ["Opp Rec", "OppReceptions", "Opp Receptions"] },
      { label: "Opp Rec TD", aliases: ["Opp Rec TD", "OppRecTD", "Opp Receiving TD"] },
    ],
  },
  WR: {
    "Player Info": [
      { label: "POS", aliases: ["POS", "Pos", "Position"] },
      { label: "Player", aliases: ["Player", "player", "Name"] },
      { label: "DK Sal", aliases: ["DK Sal", "DK Salary", "dk_sal"] },
      { label: "FD Sal", aliases: ["FD Sal", "FD Salary", "fd_sal"] },
    ],
    "Matchup Info": [
      { label: "Team", aliases: ["Team", "team"] },
      { label: "OPP", aliases: ["OPP", "Opp", "opp", "Opponent"] },
      { label: "Home", aliases: ["Home", "home"] },
      { label: "Time", aliases: ["Time", "time", "Kickoff"] },
    ],
    Vegas: [
      { label: "O/U", aliases: ["O/U", "OU", "Total", "Game Total"] },
      { label: "Imp Total", aliases: ["Imp Total", "Implied Total", "Team Total"] },
      { label: "Line", aliases: ["Line", "Spread", "Spr"] },
    ],
    "Player Stats (Per Game)": [
      { label: "Rec Yds", aliases: ["Rec Yds", "RecYds", "Receiving Yds", "ReYds"] },
      { label: "Rec", aliases: ["Rec", "Receptions", "Catches"] },
      { label: "Targets", aliases: ["Targets", "Tgts", "Tgt"] },
      { label: "TD", aliases: ["TD", "Tds", "Receiving TD"] },
      { label: "Tgt Shr", aliases: ["Tgt Shr", "Target Share", "TgtShr"] },
      { label: "MsAir", aliases: ["MsAir", "MSAir", "Air Yards Share", "AirShr"] },
      { label: "adot", aliases: ["adot", "aDOT", "ADOT"] },
    ],
    Matchup: [
      { label: "DK Pts", aliases: ["DK Pts", "DKPts", "dk_pts"] },
      { label: "Rank", aliases: ["Rank", "rank"] },
      { label: "Opp Rec Yds", aliases: ["Opp Rec Yds", "OppRecYds", "Opp Receiving Yds"] },
      { label: "OPP Rec TD", aliases: ["OPP Rec TD", "Opp Rec TD", "OppRecTD"] },
    ],
  },
  TE: {
    "Player Info": [
      { label: "POS", aliases: ["POS", "Pos", "Position"] },
      { label: "Player", aliases: ["Player", "player", "Name"] },
      { label: "DK Sal", aliases: ["DK Sal", "DK Salary", "dk_sal"] },
      { label: "FD Sal", aliases: ["FD Sal", "FD Salary", "fd_sal"] },
    ],
    "Matchup Info": [
      { label: "Team", aliases: ["Team", "team"] },
      { label: "OPP", aliases: ["OPP", "Opp", "opp", "Opponent"] },
      { label: "Home", aliases: ["Home", "home"] },
      { label: "Time", aliases: ["Time", "time", "Kickoff"] },
    ],
    Vegas: [
      { label: "O/U", aliases: ["O/U", "OU", "Total", "Game Total"] },
      { label: "Imp Total", aliases: ["Imp Total", "Implied Total", "Team Total"] },
      { label: "Line", aliases: ["Line", "Spread", "Spr"] },
    ],
    "Player Stats (Per Game)": [
      { label: "Rec Yds", aliases: ["Rec Yds", "RecYds", "Receiving Yds", "ReYds"] },
      { label: "Rec", aliases: ["Rec", "Receptions", "Catches"] },
      { label: "Targets", aliases: ["Targets", "Tgts", "Tgt"] },
      { label: "TD", aliases: ["TD", "Tds", "Receiving TD"] },
      { label: "Tgt Shr", aliases: ["Tgt Shr", "Target Share", "TgtShr"] },
      { label: "Routes%", aliases: ["Routes%", "Routes %", "Route%", "Route %"] },
      { label: "Block%", aliases: ["Block%", "Block %", "Blk%", "Blk %"] },
      { label: "MsAir", aliases: ["MsAir", "MSAir", "Air Yards Share", "AirShr"] },
      { label: "adot", aliases: ["adot", "aDOT", "ADOT"] },
    ],
    Matchup: [
      { label: "DK Pts", aliases: ["DK Pts", "DKPts", "dk_pts"] },
      { label: "Rank", aliases: ["Rank", "rank"] },
      { label: "Opp Rec Yds", aliases: ["Opp Rec Yds", "OppRecYds", "Opp Receiving Yds"] },
      { label: "Opp Rec TD", aliases: ["Opp Rec TD", "OppRecTD"] },
    ],
  },
  K: {
    "Player Info": [
      { label: "POS", aliases: ["POS", "Pos", "Position"] },
      { label: "Player", aliases: ["Player", "player", "Name"] },
      { label: "DK Sal", aliases: ["DK Sal", "DK Salary", "dk_sal"] },
      { label: "FD Sal", aliases: ["FD Sal", "FD Salary", "fd_sal"] },
    ],
    "Matchup Info": [
      { label: "Team", aliases: ["Team", "team"] },
      { label: "OPP", aliases: ["OPP", "Opp", "opp", "Opponent"] },
      { label: "Home", aliases: ["Home", "home"] },
      { label: "Time", aliases: ["Time", "time", "Kickoff"] },
    ],
    Vegas: [
      { label: "O/U", aliases: ["O/U", "OU", "Total", "Game Total"] },
      { label: "Imp Total", aliases: ["Imp Total", "Implied Total", "Team Total"] },
      { label: "Spread", aliases: ["Spread", "Line", "Spr"] },
    ],
    "Player Stats (Per Game)": [
      { label: "FGM", aliases: ["FGM", "FG Made", "FG"] },
      { label: "FGA", aliases: ["FGA", "FG Att", "FG Attempts"] },
      { label: "FG%", aliases: ["FG%", "FGP", "FG Pct", "FG %"] },
      { label: "XPM", aliases: ["XPM", "XP", "XP Made"] },
      { label: "XPA", aliases: ["XPA", "XP Att", "XP Attempts"] },
      { label: "Pts", aliases: ["Pts", "Points"] },
    ],
    Matchup: [
      { label: "DK Pts", aliases: ["DK Pts", "DKPts", "dk_pts"] },
      { label: "Rank", aliases: ["Rank", "rank"] },
    ],
  },
  DST: {
    "Player Info": [
      { label: "POS", aliases: ["POS", "Pos", "Position"] },
      { label: "Player", aliases: ["Player", "player", "Name"] },
      { label: "DK Sal", aliases: ["DK Sal", "DK Salary", "dk_sal"] },
      { label: "FD Sal", aliases: ["FD Sal", "FD Salary", "fd_sal"] },
    ],
    "Matchup Info": [
      { label: "Team", aliases: ["Team", "team"] },
      { label: "OPP", aliases: ["OPP", "Opp", "opp", "Opponent"] },
      { label: "Home", aliases: ["Home", "home"] },
      { label: "Time", aliases: ["Time", "time", "Kickoff"] },
    ],
    Vegas: [
      { label: "O/U", aliases: ["O/U", "OU", "Total", "Game Total"] },
      { label: "Imp Total", aliases: ["Imp Total", "Implied Total", "Team Total"] },
      { label: "Line", aliases: ["Line", "Spread", "Spr"] },
    ],
    "Team Stats": [
      { label: "Sack", aliases: ["Sack", "Sacks", "Sk"] },
      { label: "Int", aliases: ["Int", "INT", "Interceptions"] },
      { label: "Fum", aliases: ["Fum", "Fumbles", "Fum Rec", "Fumble Recoveries"] },
    ],
    "Opp Stats": [
      { label: "Opp Sack", aliases: ["Opp Sack", "Opp Sacks", "OppSk"] },
      { label: "Opp Int", aliases: ["Opp Int", "Opp INT", "OppInterceptions"] },
      { label: "Opp Fum", aliases: ["Opp Fum", "Opp Fumbles", "OppFum"] },
    ],
  },
};
function schemaForPos(pos) {
  return SCHEMAS[pos] || SCHEMAS.QB;
}
/**
 * Build a stable ordered list of JSON keys across all rows.
 */
function unionKeys(data) {
  if (!data?.length) return [];
  const ordered = [...Object.keys(data[0] || {})];
  const seen = new Set(ordered);
  for (const row of data) {
    if (!row) continue;
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) {
        seen.add(k);
        ordered.push(k);
      }
    }
  }
  return ordered;
}
/**
 * Resolve a schema column (label + aliases) to an actual JSON key.
 */
function resolveKey(rawCols, aliases) {
  const wants = (aliases || []).map((a) => keynorm(a));
  const map = new Map(rawCols.map((k) => [keynorm(k), k]));
  // exact normalized match first
  for (const w of wants) {
    const hit = map.get(w);
    if (hit) return hit;
  }
  // fallback: contains match (helps with mismatched names)
  for (const w of wants) {
    const hit = rawCols.find((k) => keynorm(k).includes(w) || w.includes(keynorm(k)));
    if (hit) return hit;
  }
  return null;
}
/**
 * Build final columns:
 *  - bands: [{name,start,span}]
 *  - cols: [{ label, key }]
 */
function buildColumnsFromSchema(pos, rawCols) {
  const schema = schemaForPos(pos);
  const bandNames = Object.keys(schema); // preserves order you wrote in SCHEMAS
  const cols = [];
  const bands = [];
  let start = 0;
  for (const bandName of bandNames) {
    const items = schema[bandName] || [];
    const bandStart = start;
    for (const it of items) {
      const key = resolveKey(rawCols, it.aliases);
      cols.push({ label: it.label, key });
      start += 1;
    }
    bands.push({ name: bandName, start: bandStart, span: items.length });
  }
  return { cols, bands };
}
/* ============================ PLAYER PICKER ============================ */
function useOutsideClick(ref, onClose) {
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ref, onClose]);
}
/* ============================ HEATMAP DIRECTIONS ============================ */
// Always heat O/U & Imp Total as higher = better
const ALWAYS_HEAT_HIGH = [/^o\/u$/i, /^imp[\s._-]*total$/i];
const DIR_RULES = [
  // higher is better
  { re: /^(pass|rush|rec|opp)\s.*(yds|yards)$/i, dir: "higher" },
  { re: /^(pass|rush|rec|opp)\s.*td$/i, dir: "higher" },
  { re: /^td$/i, dir: "higher" },
  { re: /^ypa$/i, dir: "higher" },
  { re: /^comp\s*pct$/i, dir: "higher" },
  { re: /^pass\s*att$/i, dir: "higher" },
  { re: /^rush\s*att$/i, dir: "higher" },
  { re: /^ypc$/i, dir: "higher" },
  { re: /^qbr$/i, dir: "higher" },
  { re: /^dk\s*pts$/i, dir: "higher" },
  { re: /^rank$/i, dir: "higher" },
  { re: /^targets?$/i, dir: "higher" },
  { re: /^tgt\s*shr$/i, dir: "higher" },
  { re: /^opr$/i, dir: "higher" },
  { re: /^msair$/i, dir: "higher" },
  { re: /^adot$/i, dir: "higher" },
  { re: /^routespct$/i, dir: "higher" },
  { re: /^rec$/i, dir: "higher" },
  { re: /^opp\s*rec$/i, dir: "higher" },
  { re: /^comppct$/i, dir: "higher" },
  { re: /^sack$|^opp\s*sack$/i, dir: "higher" },
  { re: /^fum$|^opp\s*fum$/i, dir: "higher" },
  // kickers: higher is better
  { re: /^fgm$|^fga$|^fgpct$|^xpm$|^xpa$/i, dir: "higher" },
  // lower is better
  { re: /^int$|^opp\s*int$/i, dir: "lower" },
  { re: /^dk\s*sal$|^fd\s*sal$/i, dir: "lower" },
  { re: /^blockpct$/i, dir: "lower" },
];
function directionForLabel(label, posKey) {
  const k = String(label).trim();
  // Always heat O/U & Imp Total as higher = better
  if (ALWAYS_HEAT_HIGH.some((rx) => rx.test(k))) return "higher";
  // DST overrides: INTs are good for defenses (and opponent INTs = picks thrown is also good)
  if (String(posKey).toUpperCase() === "DST") {
    if (/^int$/i.test(k)) return "higher";
    if (/^opp\s*int$/i.test(k)) return "higher";
  }
  for (const { re, dir } of DIR_RULES) {
    if (re.test(k.replace(/%/g, "pct"))) return dir;
  }
  return null;
}
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (v == null || min == null || max == null || min === max || !dir) return null;
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const isDark = theme === "dark";
  let hue;
  let sat;
  let light;
  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 80;
      light = 62 + u * 10;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 75;
      light = 72 - u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 85;
      light = 42 + u * 6;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 80;
      light = 38 - u * 4;
    }
  }
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}
async function loadPreparedGameLogs(position, year) {
  const posKey = String(position || "QB").toUpperCase();
  const season = Number(year) || DEFAULT_GAME_LOG_YEAR;
  const cacheKey = `${season}:${posKey}`;

  if (GAME_LOG_PREPARED_CACHE.has(cacheKey)) {
    return GAME_LOG_PREPARED_CACHE.get(cacheKey);
  }
  if (GAME_LOG_REQUEST_CACHE.has(cacheKey)) {
    return GAME_LOG_REQUEST_CACHE.get(cacheKey);
  }

  const request = Promise.all([
    // The main log is required; the opportunity file may be absent for older seasons.
    fetch(gameLogUrl(season, posKey), { cache: "no-store" }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }),
    fetch(opportunityUrl(season), { cache: "no-store" })
      .then((response) => response.ok ? response.json() : [])
      .catch(() => []),
  ])
    .then(([json, opportunityJson]) => {
      const rows = Array.isArray(json) ? json : json?.rows || json?.data || [];
      const opportunityRows = Array.isArray(opportunityJson)
        ? opportunityJson : opportunityJson?.rows || opportunityJson?.data || [];
      const prepared = mergeOpportunityIntoGameLogs(rows, opportunityRows, posKey);
      GAME_LOG_PREPARED_CACHE.set(cacheKey, prepared);
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      return prepared;
    })
    .catch((error) => {
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      throw error;
    });

  GAME_LOG_REQUEST_CACHE.set(cacheKey, request);
  return request;
}

function useLazyPreparedGameLogs(position, year, enabled) {
  const posKey = String(position || "QB").toUpperCase();
  const season = Number(year) || DEFAULT_GAME_LOG_YEAR;
  const cacheKey = `${season}:${posKey}`;
  const [rows, setRows] = useState(() => GAME_LOG_PREPARED_CACHE.get(cacheKey) || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const cached = GAME_LOG_PREPARED_CACHE.get(cacheKey);

    if (cached) {
      setRows(cached);
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    setRows([]);
    if (!enabled) {
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    setLoading(true);
    setError("");
    loadPreparedGameLogs(posKey, season)
      .then((prepared) => { if (alive) setRows(prepared); })
      .catch((e) => { if (alive) setError(String(e)); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [cacheKey, posKey, season, enabled]);

  return { rows, loading, error };
}


const NFL_SUFFIXES = new Set(["JR", "SR", "II", "III", "IV", "V"]);

function normalizePlayerName(name = "") {
  const parts = String(name)
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (parts.length > 1 && NFL_SUFFIXES.has(parts[parts.length - 1])) parts.pop();
  return parts.join(" ");
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function firstValue(row, keys, fallback = "") {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && row?.[k] !== "") return row[k];
  }

  // Feeds use a mix of snake_case, spaced labels, and uppercase headers.
  // Try a normalized-key lookup before giving up.
  if (row && typeof row === "object") {
    const normalized = new Map(Object.keys(row).map((key) => [keynorm(key), key]));
    for (const k of keys) {
      const actual = normalized.get(keynorm(k));
      if (actual && row[actual] !== undefined && row[actual] !== null && row[actual] !== "") return row[actual];
    }
  }
  return fallback;
}


function nullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(/[%,$]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePreparedGameLogRow(row = {}, fallbackPosition = "") {
  const position = String(firstValue(row, ["position", "pos"], fallbackPosition)).toUpperCase();
  const name = firstValue(row, ["player", "name", "player_name"], "");
  const team = fixAbbr(firstValue(row, ["team", "posteam", "recent_team"], ""));

  return {
    raw: row,
    season: num(firstValue(row, ["season"], 0)),
    week: num(firstValue(row, ["week"], 0)),
    seasonType: String(firstValue(row, ["season_type", "game_type"], "REG")),
    playerId: String(firstValue(row, ["player_id", "player_gsis_id", "gsis_id"], "") || "").trim().toLowerCase(),
    name,
    pos: position,
    team,
    opp: fixAbbr(firstValue(row, ["opponent", "opp", "opponent_team"], "")),
    result: String(firstValue(row, ["result"], "")),
    teamScore: firstValue(row, ["team_score"], null),
    opponentScore: firstValue(row, ["opponent_score"], null),
    _playerKey: normalizePlayerName(firstValue(row, ["player_key", "player", "name"], name)),

    passCmp: num(firstValue(row, ["pass_completions", "passCmp"], 0)),
    passAtt: num(firstValue(row, ["pass_attempts", "passAtt"], 0)),
    passYds: num(firstValue(row, ["passing_yards", "passYds"], 0)),
    passTd: num(firstValue(row, ["passing_tds", "passTd"], 0)),
    interceptions: num(firstValue(row, ["interceptions"], 0)),
    compPct: num(firstValue(row, ["completion_pct", "compPct"], 0)),
    ypa: num(firstValue(row, ["yards_per_attempt", "ypa"], 0)),
    sacks: num(firstValue(row, ["sacks"], 0)),

    rushAtt: num(firstValue(row, ["rushing_attempts", "rushAtt"], 0)),
    rushYds: num(firstValue(row, ["rushing_yards", "rushYds"], 0)),
    rushTd: num(firstValue(row, ["rushing_tds", "rushTd"], 0)),
    ypc: num(firstValue(row, ["yards_per_carry", "ypc"], 0)),

    targets: num(firstValue(row, ["targets"], 0)),
    rec: num(firstValue(row, ["receptions", "rec"], 0)),
    recYds: num(firstValue(row, ["receiving_yards", "recYds"], 0)),
    recTd: num(firstValue(row, ["receiving_tds", "recTd"], 0)),
    ypr: num(firstValue(row, ["yards_per_reception", "ypr"], 0)),
    airYards: num(firstValue(row, ["air_yards", "airYards"], 0)),
    targetShare: firstValue(row, ["target_share", "targetShare"], null),
    airShare: firstValue(row, ["air_yards_share", "airShare"], null),
    snapPct: firstValue(row, ["snap_pct", "snapPct"], null),
    fumblesLost: num(firstValue(row, ["fumbles_lost", "fumblesLost"], 0)),
    dk: num(firstValue(row, ["dk_points", "dk"], 0)),
    fd: num(firstValue(row, ["fd_points", "fd"], 0)),

    // Filled from ff_opportunity.json when available.
    xFp: null,
    fpDiff: null,
    opportunities: null,

    pointsAllowed: num(firstValue(row, ["points_allowed"], 0)),
    fumbleRecoveries: num(firstValue(row, ["fumble_recoveries"], 0)),
    defensiveTds: num(firstValue(row, ["defensive_tds"], 0)),
  };
}

function mergeOpportunityIntoGameLogs(logRows = [], opportunityRows = [], fallbackPosition = "") {
  const prepared = logRows.map((row) => normalizePreparedGameLogRow(row, fallbackPosition));

  const byId = new Map();
  const byName = new Map();

  for (const raw of opportunityRows || []) {
    const season = num(firstValue(raw, ["season"], 0));
    const week = num(firstValue(raw, ["week"], 0));
    if (!season || !week) continue;

    const playerId = String(
      firstValue(raw, ["player_id", "player_gsis_id", "gsis_id"], "") || ""
    ).trim().toLowerCase();

    const name = normalizePlayerName(
      firstValue(raw, ["full_name", "player", "player_name", "name"], "")
    );
    const team = fixAbbr(firstValue(raw, ["posteam", "team", "recent_team"], ""));

    const entry = {
      xFp: nullableNumber(
        firstValue(raw, ["total_fantasy_points_exp", "fantasy_points_exp", "xfp"], null)
      ),
      fpDiff: nullableNumber(
        firstValue(raw, ["total_fantasy_points_diff", "fantasy_points_diff", "fpoe"], null)
      ),
      opportunities:
        num(firstValue(raw, ["rush_attempt", "rushing_attempts"], 0)) +
        num(firstValue(raw, ["rec_attempt", "targets"], 0)),
    };

    if (playerId) {
      byId.set(`${season}__${week}__${playerId}`, entry);
    }

    if (name) {
      byName.set(`${season}__${week}__${team}__${name}`, entry);
      byName.set(`${season}__${week}____${name}`, entry);
    }
  }

  return prepared.map((row) => {
    let opportunity = null;

    if (row.playerId) {
      opportunity = byId.get(`${row.season}__${row.week}__${row.playerId}`) || null;
    }

    if (!opportunity) {
      opportunity =
        byName.get(`${row.season}__${row.week}__${row.team}__${row._playerKey}`) ||
        byName.get(`${row.season}__${row.week}____${row._playerKey}`) ||
        null;
    }

    if (!opportunity) return row;

    return {
      ...row,
      xFp: opportunity.xFp,
      fpDiff: opportunity.fpDiff,
      opportunities: opportunity.opportunities,
    };
  });
}

function pct(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const x = Math.abs(n) <= 1.5 ? n * 100 : n;
  return `${x.toFixed(1).replace(/.0$/, "")}%`;
}

function one(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1).replace(/.0$/, "") : "—";
}



function GameLogModal({ player, position, preparedRows = [], loading, error, onClose, isDark, gameLogYear, gameLogYears, onGameLogYearChange }) {
  const logs = useMemo(() => {
    const wanted = normalizePlayerName(player);
    return preparedRows
      .filter((r) => {
        const rowKey = r._playerKey || normalizePlayerName(
          firstValue(r, ["player_key", "player", "name", "full_name", "player_display_name"], "")
        );
        const rowPos = String(firstValue(r, ["pos", "position", "player_position"], "")).toUpperCase();
        return rowKey === wanted && (!rowPos || rowPos === position || (position === "RB" && rowPos === "FB"));
      })
      .sort((a, b) => a.season - b.season || a.week - b.week);
  }, [preparedRows, player, position]);

  useEffect(() => {
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const logNumber = (value) => value == null || value === "" ? "—" : one(value);
  const avg = (k) => {
    const values = logs.filter((r) => r[k] != null && r[k] !== "").map((r) => Number(r[k])).filter(Number.isFinite);
    return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
  };
  const signed = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return "—";
    const n = Number(v);
    const body = Math.abs(n).toFixed(1).replace(/.0$/, "");
    return n > 0 ? `+${body}` : n < 0 ? `-${body}` : "0";
  };
  const fpoeCell = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return <span className="text-slate-500">—</span>;
    const n = Number(v);
    const tone = n > 0.05 ? (isDark ? "text-emerald-400" : "text-emerald-700") : n < -0.05 ? (isDark ? "text-rose-400" : "text-rose-700") : C.muted;
    return <span className={`font-extrabold ${tone}`}>{signed(n)}</span>;
  };
  const team = logs[0]?.team || "";
  const C = isDark
    ? { shell: "bg-slate-950 border-slate-700 text-slate-100", muted: "text-slate-400", head: "bg-slate-900", row: "border-slate-800 hover:bg-sky-500/10", card: "bg-slate-900/70 border-slate-800" }
    : { shell: "bg-white border-slate-300 text-slate-900", muted: "text-slate-500", head: "bg-slate-100", row: "border-slate-200 hover:bg-sky-50", card: "bg-slate-50 border-slate-200" };

  const common = [
    ["Season", r => r.season],
    ["Wk", r => r.week],
    ["Opp", r => (
      <div className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none">
        <TeamWithLogo code={r.opp} />
        {r.result && Number.isFinite(r.teamScore) && Number.isFinite(r.opponentScore) ? (
          <span
            className={`text-[9px] font-extrabold ${
              r.result === "W"
                ? "text-emerald-400"
                : r.result === "L"
                  ? "text-red-400"
                  : "text-slate-400"
            }`}
            title={`${r.result} ${r.teamScore}-${r.opponentScore}`}
          >
            {r.result} {r.teamScore}–{r.opponentScore}
          </span>
        ) : null}
      </div>
    )],
  ];
  const fantasy = [["DK", r => logNumber(r.dk)], ["FD", r => logNumber(r.fd)], ["xFP", r => r.xFp == null ? "—" : logNumber(r.xFp)], ["FPOE", r => fpoeCell(r.fpDiff)]];
  const passing = [
    ["Cmp", (r) => r.passCmp], ["Att", (r) => r.passAtt], ["Cmp%", (r) => pct(r.compPct)],
    ["Yds", (r) => r.passYds], ["TD", (r) => r.passTd],
    ["INT", (r) => r.interceptions], ["YPA", (r) => logNumber(r.ypa)],
  ];
  const rushing = [
    ["Att", (r) => r.rushAtt], ["Yds", (r) => r.rushYds], ["TD", (r) => r.rushTd],
    ...(position === "RB" ? [["YPC", (r) => logNumber(r.ypc)]] : []),
  ];
  const receiving = [
    ["Tgt", (r) => r.targets], ["Rec", (r) => r.rec], ["Yds", (r) => r.recYds],
    ["TD", (r) => r.recTd], ["YPR", (r) => logNumber(r.ypr)],
    ...(position !== "RB" ? [["Air Yds", (r) => r.airYards]] : []),
  ];
  const usage = [
    ...(position === "RB" ? [["Rush + Tgts", (r) =>
      r.opportunities == null ? r.rushAtt + r.targets : r.opportunities,
      "Rushing attempts plus targets"]] : []),
    ["Tgt%", (r) => pct(r.targetShare), "Share of team targets"],
    ...(position !== "RB" ? [["Air%", (r) => pct(r.airShare), "Share of team air yards"]] : []),
    ["Snap%", (r) => pct(r.snapPct), "Offensive snap share"],
  ];
  const groups = [
    { label: "Game", tone: isDark ? "text-slate-400 bg-slate-800/50" : "text-slate-600 bg-slate-200/60", columns: common },
    ...(position === "QB" ? [
      { label: "Passing", tone: isDark ? "text-sky-300 bg-sky-500/10" : "text-sky-700 bg-sky-500/10", columns: passing },
      { label: "Rushing", tone: isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-500/10", columns: rushing },
    ] : [
      ...(position === "RB" ? [{ label: "Rushing", tone: isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-500/10", columns: rushing }] : []),
      { label: "Receiving", tone: isDark ? "text-violet-300 bg-violet-500/10" : "text-violet-700 bg-violet-500/10", columns: receiving },
      { label: "Usage", tone: isDark ? "text-slate-300 bg-slate-800/60" : "text-slate-600 bg-slate-200/60", columns: usage },
    ]),
    { label: "Fantasy", tone: isDark ? "text-amber-300 bg-amber-500/10" : "text-amber-700 bg-amber-500/10", columns: fantasy },
  ];
  const columns = groups.flatMap((group) => group.columns.map(([label, render, title], index) => ({
    label, render, title: title || `${group.label}: ${label}`, group: group.label, start: index === 0,
  })));

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-2 md:p-5" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={`${player} game log`} className={`w-full max-w-[1500px] max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border shadow-2xl ${C.shell}`}>
        <div className="flex shrink-0 flex-wrap items-start justify-between gap-4 border-b border-current/10 px-4 py-3 md:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {team ? <img src={`${LOGO_BASE}/${team}.${LOGO_EXT}`} className="h-7 w-7 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} /> : null}
              <h2 className="truncate text-lg md:text-2xl font-extrabold">{player}</h2>
              <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-bold text-sky-400">{position}</span>
            </div>
            <p className={`mt-0.5 text-xs ${C.muted}`}>{gameLogYear} season / Weekly game log / Week 1 first</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className={`flex h-9 items-center gap-2 rounded-lg border px-3 ${C.card}`}>
              <span className={`text-[10px] font-bold uppercase tracking-wide ${C.muted}`}>Season</span>
              <select
                value={gameLogYear}
                onChange={(e) => onGameLogYearChange(Number(e.target.value))}
                className={
                  "bg-transparent text-sm font-bold outline-none " +
                  (isDark ? "text-slate-100" : "text-slate-900")
                }
              >
                {gameLogYears.map((year) => (
                  <option
                    key={year}
                    value={year}
                    className={isDark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-900"}
                  >
                    {year}
                  </option>
                ))}
              </select>
            </label>
            <button onClick={onClose} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border ${C.card}`} aria-label="Close game log">✕</button>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 px-3 py-3 sm:grid-cols-4 md:px-5">
          {[
            ["Games", logs.length],
            ["Avg DK", logNumber(avg("dk"))],
            ["Avg xFP", avg("xFp") == null ? "—" : logNumber(avg("xFp"))],
            [position === "QB" ? "Avg Pass Yds" : position === "RB" ? "Rush + Tgts/G" : "Avg Targets", position === "QB" ? logNumber(avg("passYds")) : position === "RB" ? one(logs.length ? logs.reduce((sum, r) => sum + Number(r.opportunities == null ? r.rushAtt + r.targets : r.opportunities), 0) / logs.length : 0) : logNumber(avg("targets"))],
          ].map(([label, value]) => (
            <div key={label} className={`rounded-xl border px-3 py-2 ${C.card}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wide ${C.muted}`}>{label}</div>
              <div className="text-lg font-extrabold leading-tight">{loading ? "—" : value}</div>
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-t border-current/10">
          {loading ? <div className={`p-8 text-center text-sm ${C.muted}`}>Loading game logs…</div> : error ? <div className="p-8 text-center text-sm text-red-400">Unable to load game logs. Please try again.</div> : logs.length === 0 ? <div className={`p-8 text-center text-sm ${C.muted}`}>No weekly game logs found for this player.</div> : (
            <table className="w-max min-w-full border-separate border-spacing-0 text-[11px] tabular-nums md:text-xs">
              <caption className="sr-only">{player} weekly game log for {gameLogYear}, earliest week first</caption>
              <thead className={`sticky top-0 z-20 ${C.head}`}>
                <tr>
                  {groups.map((group, index) => (
                    <th key={group.label} scope="colgroup" colSpan={group.columns.length}
                      className={`h-8 border-b border-current/10 px-3 text-center text-[10px] font-extrabold uppercase tracking-[0.14em] ${group.tone} ${index ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""}`}>
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map(({ label, title, group, start }, i) => (
                    <th key={`${group}-${label}-${i}`} scope="col" title={title}
                      className={`h-9 whitespace-nowrap border-b ${isDark ? "border-slate-700" : "border-slate-300"} ${C.head} px-3 text-center font-bold ${start && i ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""} ${
                        label === "DK" || label === "FD" ? (isDark ? "text-sky-400" : "text-sky-700") :
                        label === "xFP" || label === "FPOE" ? (isDark ? "text-amber-300" : "text-amber-700") : (isDark ? "text-slate-300" : "text-slate-600")
                      }`}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((r, ri) => (
                  <tr key={`${r.season}-${r.week}-${ri}`} className={`${ri % 2 ? (isDark ? "bg-slate-900/40" : "bg-slate-50") : ""} transition-colors ${C.row}`}>
                    {columns.map(({ label, render, group, start }, ci) => (
                      <td key={`${group}-${label}-${ci}`}
                        className={`h-10 whitespace-nowrap border-b ${isDark ? "border-slate-800/70" : "border-slate-200"} px-3 text-center ${start && ci ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""} ${
                          label === "DK" || label === "FD" ? (isDark ? "bg-sky-500/[0.04] font-extrabold text-sky-400" : "bg-sky-500/[0.04] font-extrabold text-sky-700") :
                          label === "xFP" ? (isDark ? "bg-amber-500/[0.04] font-extrabold text-amber-300" : "bg-amber-500/[0.04] font-extrabold text-amber-700") : ""
                        }`}>
                        {render(r) ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={`flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-current/10 px-4 py-2.5 text-[10px] leading-relaxed md:px-5 ${C.card} ${C.muted}`}>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><span className={`font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>xFP</span> Expected fantasy points</span>
            <span><span className={`font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>FPOE</span> Fantasy points over expected</span>
            <span><span className="text-emerald-400">+ Above</span> / <span className="text-rose-400">− Below</span> expected</span>
          </div>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}


/* ============================ MAIN ============================ */
export default function NflPosDataShowdown({ pos = "QB" }) {
  const key = String(pos || "QB").toUpperCase();
  const src = DATA_SOURCES[key] || DATA_SOURCES.QB;
  const title = TITLES[key] || "NFL — Data (Showdown)";
  const { data, loading, err } = useJson(src);
  const meta = useMeta(META_URL);
  const [activePlayer, setActivePlayer] = useState(null);
  const [gameLogYear, setGameLogYear] = useState(DEFAULT_GAME_LOG_YEAR);
  const supportsGameLogs = ["QB", "RB", "WR", "TE"].includes(key);
  const {
    rows: preparedGameLogRows,
    loading: gameLogsLoading,
    error: gameLogsError,
  } = useLazyPreparedGameLogs(key, gameLogYear, supportsGameLogs && Boolean(activePlayer));
  useEffect(() => { setActivePlayer(null); }, [key]);
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const T = isDark ? DARK_THEME : LIGHT_THEME;
  const updatedStamp = useMemo(() => {
    const art = meta?.artifacts?.find((x) => String(x.path || "").endsWith(src));
    const raw = art?.last_updated || art?.tags?.last_updated || meta?.last_updated;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [meta, src]);
  const rawCols = useMemo(() => unionKeys(data), [data]);
  // ✅ EXACT columns & exact band labels
  const { cols, bands } = useMemo(() => buildColumnsFromSchema(key, rawCols), [key, rawCols]);
  // search + player picker
  const [q, setQ] = useState("");
  const allPlayers = useMemo(() => {
    const s = new Set();
    for (const r of data) {
      const n = r?.Player || r?.player || r?.Name;
      if (n) s.add(String(n));
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [data]);
  const [selected, setSelected] = useState(new Set()); // empty => all
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickFilter, setPickFilter] = useState("");
  const pickerRef = useRef(null);
  useOutsideClick(pickerRef, () => setPickerOpen(false));
  const toggleOne = (name) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  const filtered = useMemo(() => {
    const s = lower(q);
    const restrict = selected.size > 0;
    return data.filter((r) => {
      const name = String(r?.Player || r?.player || r?.Name || "");
      if (restrict && !selected.has(name)) return false;
      if (!s) return true;
      const t = lower(r?.Team || r?.team);
      const o = lower(r?.OPP || r?.Opp || r?.opp || r?.Opponent);
      return lower(name).includes(s) || t.includes(s) || o.includes(s);
    });
  }, [data, q, selected]);
  // heatStats computed from displayed columns that have numeric values
  const heatStats = useMemo(() => {
    const stats = {};
    if (!filtered.length) return stats;
    for (const c of cols) {
      const dir = directionForLabel(c.label, key);
      if (!dir || !c.key) continue;
      let min = Infinity,
        max = -Infinity;
      for (const row of filtered) {
        const n = parseNumericLike(row?.[c.key]);
        if (n == null) continue;
        if (n < min) min = n;
        if (n > max) max = n;
      }
      if (min !== Infinity && max !== -Infinity) stats[c.label] = { min, max, dir };
    }
    return stats;
  }, [filtered, cols]);
  // default sort: DK Sal if available, else Player
  const [sort, setSort] = useState({ label: "Player", dir: "asc" });
  useEffect(() => {
    const dk = cols.find((c) => c.label === "DK Sal");
    if (dk) setSort({ label: "DK Sal", dir: "desc" });
    else setSort({ label: "Player", dir: "asc" });
  }, [cols]);
  const onSort = (label) => {
    setSort((prev) =>
      prev.label === label
        ? { label, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { label, dir: "desc" }
    );
  };
  function compareCells(a, b, col) {
    const { label, key: k } = col;
    if (!k) return 0; // column exists in header but not in data
    if (/^time$/i.test(label)) {
      const av = timeToMinutes(a[k]);
      const bv = timeToMinutes(b[k]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    }
    if (/\b(sal|salary)\b/i.test(label)) {
      const av = parseNumericLike(a[k]);
      const bv = parseNumericLike(b[k]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    }
    const av = parseNumericLike(a[k]);
    const bv = parseNumericLike(b[k]);
    if (av != null && bv != null) return av - bv;
    const sa = String(a[k] ?? "");
    const sb = String(b[k] ?? "");
    return sa.localeCompare(sb, undefined, { sensitivity: "base" });
  }
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const sortCol = cols.find((c) => c.label === sort.label) || cols[0];
    const sgn = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => sgn * compareCells(a, b, sortCol));
    return arr;
  }, [filtered, cols, sort]);
  const cellCls = `${CELL_PAD} text-center whitespace-nowrap overflow-hidden text-ellipsis ${T.text}`;
  const headerCls = `${HEADER_PAD} font-semibold text-center whitespace-nowrap cursor-pointer select-none ${T.text}`;
  return (
    <div className={T.page}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">{title}</h1>
            <span className={`text-xs italic ${T.subtext}`}>Updated: {updatedStamp ?? "—"}</span>
          </div>
          {/* Theme toggle */}
          <div className="flex items-center gap-3">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </header>

        <nav aria-label="Player data positions" className={`${T.radius} ${T.card} mb-3 p-3`}>
          <div className={`inline-flex border-b ${T.border}`}>
              <div className="flex flex-wrap items-center gap-6">
                {["QB", "RB", "WR", "TE", "K", "DST"].map((p) => (
                  <a
                    key={p}
                    href={`/nfl/showdown/player-data/${p.toLowerCase()}`}
                    aria-current={key === p ? "page" : undefined}
                    className={`relative inline-flex h-9 items-center pb-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${key === p ? (isDark ? "text-white" : "text-slate-900") + " after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400" : "text-slate-500 hover:text-sky-500"}`}
                  >
                    {p}
                  </a>
                ))}
              </div>          </div>
        </nav>

        <section aria-label="Player data" className={`${T.radius} ${T.card}`}>
          <div className={`border-b p-3 ${T.border}`}>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">Player Data</h2>
              <span className={`text-xs ${T.subtext}`}>{!loading && !err && <>{sorted.length.toLocaleString()} of {data.length.toLocaleString()} players</>}</span>
            </div>
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Player picker */}
          <div className="relative" ref={pickerRef}>
            <button
              className={
                "h-8 rounded-md px-3 text-xs border " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100"
                  : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50")
              }
              onClick={() => setPickerOpen((v) => !v)}
            >
              {selected.size === 0 ? "All players" : `${selected.size} selected`}
            </button>
            {pickerOpen && (
              <div
                className={
                  "absolute left-0 top-full mt-2 z-50 w-80 max-w-[calc(100vw-4rem)] rounded-xl border shadow-xl p-2 " +
                  (isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900")
                }
              >
                <div className="mb-2 flex items-center gap-2">
                  <button
                    onClick={() => setSelected(new Set(allPlayers))}
                    className={
                      "px-2 py-1 text-xs rounded border " +
                      (isDark ? "border-slate-600 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-100")
                    }
                  >
                    All
                  </button>
                  <button
                    onClick={() => setSelected(new Set())}
                    className={
                      "px-2 py-1 text-xs rounded border " +
                      (isDark ? "border-slate-600 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-100")
                    }
                  >
                    None
                  </button>
                  <input
                    value={pickFilter}
                    onChange={(e) => setPickFilter(e.target.value)}
                    placeholder="Search players…"
                    className={
                      "ml-auto h-7 w-40 rounded border px-2 text-xs focus:outline-none " +
                      (isDark
                        ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                        : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400")
                    }
                  />
                </div>
                <div className="max-h-72 overflow-auto pr-1">
                  {(pickFilter ? allPlayers.filter((n) => lower(n).includes(lower(pickFilter))) : allPlayers).map((name) => (
                    <label key={name} className="flex items-center gap-2 py-1 text-xs">
                      <input
                        type="checkbox"
                        checked={selected.size === 0 ? true : selected.has(name)}
                        onChange={() => toggleOne(name)}
                      />
                      <span className="truncate">{name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* search */}
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search player / team / opp…"
            className={
              "h-8 w-full sm:ml-auto sm:w-72 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
              (isDark
                ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400")
            }
          />
        </div>
          </div>
        {/* TABLE */}
        <div className="overflow-auto max-h-[70vh] rounded-b-2xl overscroll-contain">
          <table className={`w-max min-w-full border-separate ${TEXT_SZ}`} style={{ borderSpacing: 0 }}>
            {cols.length > 0 && (
              <thead>
                {/* band row */}
                <tr className={`${T.bandBg} text-[11px] font-extrabold uppercase text-black`}>
                  {bands.map((g, idx) => (
                    <th
                      key={`${g.name}-${g.start}`}
                      colSpan={g.span}
                      className={`px-2 py-1 text-center border-b ${T.bandBorder} ${
                        idx === 0 ? "first:rounded-tl-2xl" : ""
                      } ${idx === bands.length - 1 ? "last:rounded-tr-2xl" : ""}`}
                    >
                      {HIDE_PLAYER_INFO_LABEL && g.name === "Player Info" ? "" : g.name}
                    </th>
                  ))}
                </tr>
                {/* headers */}
                <tr className={`${T.headerBg} sticky top-0 z-30`}>
                  {cols.map((c, i) => {
                    const isBandEnd = bands.some((b) => b.start + b.span - 1 === i);
                    const isPlayer = keynorm(c.label) === keynorm("Player");
                    return (
                      <th
                        key={`${c.label}-${i}`}
                        className={[
                          headerCls,
                          isBandEnd ? `border-r-2 ${T.bandBorder}` : `border-r ${T.border}`,
                          isPlayer ? `text-center sticky left-0 z-40 ${T.headerBg}` : "",
                        ].join(" ")}
                        onClick={() => onSort(c.label)}
                        title={c.key ? `Key: ${c.key}` : "No matching key found in JSON"}
                      >
                        <span className="inline-flex items-center gap-1">
                          <span>{c.label}</span>
                          {sort.label === c.label ? (
                            <span className="text-slate-400">{sort.dir === "desc" ? "▼" : "▲"}</span>
                          ) : (
                            <span className="text-slate-500/30">▲</span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody>
              {loading && (
                <tr>
                  <td className={`${CELL_PAD} text-center ${T.subtext}`} colSpan={cols.length}>
                    Loading…
                  </td>
                </tr>
              )}
              {err && (
                <tr>
                  <td className={`${CELL_PAD} text-center text-red-400`} colSpan={cols.length}>
                    Failed to load: {err}
                  </td>
                </tr>
              )}
              {!loading &&
                !err &&
                sorted.map((row, rIdx) => (
                  <tr
                    key={rIdx}
                    className={`${rIdx % 2 ? T.zebraEven : T.zebraOdd} hover:bg-sky-500/10 transition-colors`}
                  >
                    {cols.map((c, i) => {
                      const k = c.key;
                      const raw = k ? row?.[k] : "";
                      const isTeam = keynorm(c.label) === keynorm("Team");
                      const isOpp = keynorm(c.label) === keynorm("OPP");
                      const isPlayer = keynorm(c.label) === keynorm("Player");
                      const content =
                        isTeam || isOpp ? <TeamWithLogo code={raw} /> : fmtCellValue(c.label, raw);
                      const borders = bands.some((b) => b.start + b.span - 1 === i)
                        ? `border-r-2 ${T.bandBorder}`
                        : `border-r ${T.border}`;
                      const stat = heatStats[c.label];
                      const numVal = parseNumericLike(raw);
                      const textColor =
                        stat && k
                          ? heatTextColor(stat.min, stat.max, numVal, stat.dir, theme)
                          : null;
                      if (isPlayer) {
                        return (
                          <td
                            key={`${c.label}-${rIdx}`}
                            className={[
                              `${CELL_PAD} text-center font-medium whitespace-nowrap overflow-hidden text-ellipsis ${T.text}`,
                              borders,
                              `sticky left-0 z-20 ${rIdx % 2 ? T.zebraEven : T.zebraOdd}`,
                              "min-w-[16ch] md:min-w-[18ch]",
                              "shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)]",
                            ].join(" ")}
                            title={String(raw ?? "")}
                            style={{ backgroundColor: undefined }}
                          >
                            {supportsGameLogs ? (
                            <button type="button"
                              onClick={() => setActivePlayer(String(raw || ""))}
                              className="font-semibold text-inherit underline decoration-current/60 underline-offset-2 hover:decoration-2"
                              title={`Open ${String(raw || "")} game log`}>
                              {content}
                            </button>
                          ) : content}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={`${c.label}-${rIdx}`}
                          className={[cellCls, borders].join(" ")}
                          style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              {!loading && !err && sorted.length === 0 && (
                <tr>
                  <td className={`${CELL_PAD} text-center ${T.subtext}`} colSpan={cols.length}>
                    No rows match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </section>
      </div>
      {activePlayer && supportsGameLogs ? (
        <GameLogModal
          player={activePlayer}
          position={key}
          preparedRows={preparedGameLogRows}
          loading={gameLogsLoading}
          error={gameLogsError}
          onClose={() => setActivePlayer(null)}
          isDark={isDark}
          gameLogYear={gameLogYear}
          gameLogYears={GAME_LOG_YEARS}
          onGameLogYearChange={setGameLogYear}
        />
      ) : null}
    </div>
  );
}
